import React from "react";
import { Icon, Button, HStack } from "@chakra-ui/react";
import { studentStatusMap } from "../../../services/constants";
import useThemeColors from "../../../hooks/useThemeColors";
import useIcons from "../../../hooks/useIcons";
import { useRoleAccess } from "../../../hooks/useRoleAccess";
import CustomMenu from "../../CustomUi/CustomMenu";
import TextInput from "../../CustomUi/TextInput";
import SelectInput from "../../CustomUi/SelectInput";
import Cad from "../../CustomUi/Cad";
import { Role } from "../../../services/Enums";

interface Props {
  searchQuery: string;
  selectedClass: string;
  selectedStatus: number | null;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClassChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleStatusChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  classes: any[];
  onOpen: () => void;
  sortDate: boolean;
  handleSortDate: () => void;
  handleBulkAddOpen: () => void;
  handleDownloadTemplate: () => void;
  isActionLoading: boolean;
}

const StudentTopMenu: React.FC<Props> = ({
  searchQuery,
  selectedClass,
  selectedStatus,
  handleSearchChange,
  handleClassChange,
  handleStatusChange,
  classes,
  onOpen,
  sortDate,
  handleSortDate,
  handleBulkAddOpen,
  handleDownloadTemplate,
  isActionLoading,
}: Props) => {
  const colors = useThemeColors();
  const icons = useIcons();
  const { hasRoleOrHigher } = useRoleAccess();

  const classOptions = classes.map((cls) => ({
    label: cls.className,
    value: cls.id,
  }));

  const statusOptions = Object.entries(studentStatusMap).map(
    ([statusKey, statusValue]) => ({
      label: statusValue.name,
      value: statusKey,
    })
  );

  return (
    <Cad
      h="auto"
      flexDirection={{ base: "column", md: "row" }}
      justifyContent={"space-between"}
      alignItems={{ base: "auto", md: "flex-start" }}
    >
      <HStack
        gap={2}
        flexDirection={{ base: "column", md: "row" }}
        alignItems={{ base: "stretch", md: "center" }}
        justifyContent={"start"}
        wrap={"wrap"}
      >
        <TextInput
          label=""
          name="search"
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by name"
          startElement={<Icon as={icons.utility.search} />}
          w={{ base: "100%", md: "250px" }}
        />

        <SelectInput
          label=""
          name="class"
          value={selectedClass}
          onChange={(e) => handleClassChange(e as any)}
          collection={classOptions}
          placeholder="All Classes"
          w={{ base: "100%", md: "150px" }}
        />

        <SelectInput
          label=""
          name="status"
          value={selectedStatus?.toString()}
          onChange={(e) => handleStatusChange(e as any)}
          collection={statusOptions}
          placeholder="All Status"
          w={{ base: "100%", md: "150px" }}
        />

        <Button onClick={handleSortDate} variant="outline" size="md">
          <Icon as={sortDate ? icons.utility.sortDown : icons.utility.sortUp} />
          {sortDate ? "Last Added" : "First Added"}
        </Button>
      </HStack>

      {hasRoleOrHigher(Role.Editor) && (
        <CustomMenu
          trigger={
            <Button
              colorPalette={colors.schoolAccent.primary}
              variant="solid"
              loading={isActionLoading}
              _hover={{
                transform: "translateY(-1px)",
              }}
              _active={{
                transform: "translateY(0)",
              }}
              transition="all 0.2s"
              shadow="sm"
              w={{ base: "100%", md: "auto" }}
            >
              Actions
              <Icon as={icons.utility.chevronDown} />
            </Button>
          }
          menuItems={[
            {
              label: "Add Student",
              value: "addStudent",
              icon: icons.people.student,
              onClick: onOpen,
            },
            {
              label: "Download Template",
              value: "downloadTemplate",
              icon: icons.utility.download,
              onClick: handleDownloadTemplate,
            },
            {
              label: "Import Students",
              value: "importStudents",
              icon: icons.people.students,
              onClick: handleBulkAddOpen,
            },
          ]}
        />
      )}
    </Cad>
  );
};

export default StudentTopMenu;
